import * as Yup from 'yup'
import { useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
// @mui
import LoadingButton from '@mui/lab/LoadingButton'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import LinearProgress from '@mui/material/LinearProgress'
import { IconButton, InputAdornment } from '@mui/material'
// components
import { useSnackbar } from 'src/components/snackbar'
import FormProvider, { RHFTextField } from 'src/components/hook-form'
import axios, { API_ENDPOINTS } from 'src/utils/axios'
import { useBoolean } from 'src/hooks/use-boolean'
import Iconify from 'src/components/iconify'

type FormValues = {
  current: string
  password: string
  confirm: string
}

const passwordStrength = (pwd: string) => {
  let score = 0
  if (pwd.length >= 8) score += 1
  if (pwd.length >= 12) score += 1
  if (/[A-Z]/.test(pwd)) score += 1
  if (/[a-z]/.test(pwd)) score += 1
  if (/\d/.test(pwd)) score += 1
  if (/[^a-zA-Z0-9]/.test(pwd)) score += 1
  return score
}

const STRENGTH_INFO = [
  { label: 'Muito fraca', color: 'error.main', value: 10 },
  { label: 'Fraca', color: 'error.main', value: 25 },
  { label: 'Razoável', color: 'warning.main', value: 45 },
  { label: 'Boa', color: 'warning.main', value: 65 },
  { label: 'Forte', color: 'success.main', value: 85 },
  { label: 'Muito forte', color: 'success.main', value: 100 },
]

export default function AccountSecurity() {
  const { enqueueSnackbar } = useSnackbar()

  const showCurrent = useBoolean()
  const showNew = useBoolean()
  const showConfirm = useBoolean()

  const Schema = Yup.object().shape({
    current: Yup.string().required('Informe sua senha atual'),
    password: Yup.string()
      .required('Informe a nova senha')
      .min(8, 'A senha deve ter pelo menos 8 caracteres')
      .notOneOf([Yup.ref('current')], 'A nova senha precisa ser diferente da atual'),
    confirm: Yup.string()
      .required('Confirme a nova senha')
      .oneOf([Yup.ref('password')], 'As senhas não coincidem'),
  })

  const methods = useForm<FormValues>({
    resolver: yupResolver(Schema),
    defaultValues: { current: '', password: '', confirm: '' },
  })

  const {
    reset,
    watch,
    handleSubmit,
    formState: { isSubmitting },
  } = methods

  const newPassword = watch('password')
  const score = passwordStrength(newPassword || '')
  const strengthIdx = Math.max(0, Math.min(STRENGTH_INFO.length - 1, score - 1))
  const strength = newPassword ? STRENGTH_INFO[strengthIdx] : null

  const onSubmit = useCallback(
    async (data: FormValues) => {
      try {
        await axios.put(API_ENDPOINTS.user.editPassword, {
          current: data.current,
          password: data.password,
        })
        reset({ current: '', password: '', confirm: '' })
        enqueueSnackbar('Senha alterada com sucesso!', { variant: 'success' })
      } catch (error: any) {
        const message =
          error?.error ||
          error?.message ||
          error?.response?.data?.error ||
          'Não foi possível alterar a senha. Verifique a senha atual.'
        enqueueSnackbar(message, { variant: 'error' })
      }
    },
    [enqueueSnackbar, reset],
  )

  const requirements = [
    { ok: (newPassword?.length || 0) >= 8, label: 'Mínimo de 8 caracteres' },
    { ok: /[A-Z]/.test(newPassword || ''), label: 'Uma letra maiúscula' },
    { ok: /[a-z]/.test(newPassword || ''), label: 'Uma letra minúscula' },
    { ok: /\d/.test(newPassword || ''), label: 'Um número' },
    { ok: /[^a-zA-Z0-9]/.test(newPassword || ''), label: 'Um caractere especial' },
  ]

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Box display='grid' gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={3}>
        <Card sx={{ p: 3, borderRadius: 2 }}>
          <Stack direction='row' alignItems='center' spacing={1.5} sx={{ mb: 3 }}>
            <Iconify icon='solar:lock-password-bold-duotone' width={24} color='primary.main' />
            <Typography variant='h6' fontWeight={700}>
              Alterar senha
            </Typography>
          </Stack>

          <Stack spacing={2.5}>
            <RHFTextField
              name='current'
              label='Senha atual'
              autoComplete='current-password'
              type={showCurrent.value ? 'text' : 'password'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton onClick={showCurrent.onToggle} edge='end'>
                      <Iconify
                        icon={showCurrent.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'}
                      />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <RHFTextField
              name='password'
              label='Nova senha'
              autoComplete='new-password'
              type={showNew.value ? 'text' : 'password'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton onClick={showNew.onToggle} edge='end'>
                      <Iconify
                        icon={showNew.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'}
                      />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {newPassword && strength && (
              <Box>
                <Stack direction='row' justifyContent='space-between' sx={{ mb: 0.5 }}>
                  <Typography variant='caption' color='text.secondary'>
                    Força da senha
                  </Typography>
                  <Typography variant='caption' fontWeight={600} sx={{ color: strength.color }}>
                    {strength.label}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant='determinate'
                  value={strength.value}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: 'action.hover',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 3,
                      bgcolor: strength.color,
                    },
                  }}
                />
              </Box>
            )}

            <RHFTextField
              name='confirm'
              label='Confirmar nova senha'
              autoComplete='new-password'
              type={showConfirm.value ? 'text' : 'password'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton onClick={showConfirm.onToggle} edge='end'>
                      <Iconify
                        icon={showConfirm.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'}
                      />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Stack direction='row' justifyContent='flex-end' sx={{ pt: 1 }}>
              <LoadingButton
                type='submit'
                variant='contained'
                size='large'
                loading={isSubmitting}
                startIcon={<Iconify icon='solar:shield-check-bold' />}
              >
                Atualizar senha
              </LoadingButton>
            </Stack>
          </Stack>
        </Card>

        <Stack spacing={2}>
          <Card sx={{ p: 3, borderRadius: 2 }}>
            <Stack direction='row' alignItems='center' spacing={1.5} sx={{ mb: 2 }}>
              <Iconify icon='solar:checklist-minimalistic-bold-duotone' width={22} />
              <Typography variant='subtitle1' fontWeight={700}>
                Requisitos da senha
              </Typography>
            </Stack>
            <Stack spacing={1}>
              {requirements.map((r) => (
                <Stack key={r.label} direction='row' alignItems='center' spacing={1}>
                  <Iconify
                    icon={r.ok ? 'solar:check-circle-bold' : 'solar:close-circle-bold'}
                    width={18}
                    sx={{ color: r.ok ? 'success.main' : 'text.disabled' }}
                  />
                  <Typography
                    variant='body2'
                    sx={{
                      color: r.ok ? 'text.primary' : 'text.secondary',
                      textDecoration: r.ok ? 'none' : 'none',
                    }}
                  >
                    {r.label}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Card>

          <Alert severity='info' variant='outlined' icon={<Iconify icon='solar:info-circle-bold' />}>
            <Typography variant='subtitle2' gutterBottom>
              Dicas de segurança
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              Use senhas longas e únicas. Evite reutilizar senhas de outros sites e nunca compartilhe
              suas credenciais por mensagem.
            </Typography>
          </Alert>
        </Stack>
      </Box>
    </FormProvider>
  )
}
